import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Autocomplete, InputLabel, MenuItem, Select as MuiSelect, TextField } from '@mui/material';
import { HasAccessTo } from 'app/components/Authorization';
import { ActionButton, ConfirmDialog, SearchInput } from 'app/components';
import { EVENT } from 'app/common/Authorization/entities';
import { EDIT } from 'app/common/Authorization/permissions';
import { TranslationKeys } from 'app/translations';
import { PENSIOENBIJ_STATUS_ALL } from '../../Ducks';
import { DivRowStyled, DropdownStyled, FormControlStyled, SpacerStyled } from './EventsToolbar.styles';
const PENSIOENBIJ_STATUS_OPTIONS = [
  { value: PENSIOENBIJ_STATUS_ALL, labelKey: TranslationKeys.all },
  { value: 'pending', labelKey: TranslationKeys.pending },
  { value: 'failed', labelKey: TranslationKeys.failed },
  { value: 'complete', labelKey: TranslationKeys.complete },
] as const;
const MUTATION_TYPE_OPTIONS = [
  { value: '', labelKey: TranslationKeys.all },
  { value: 'newPerson', labelKey: TranslationKeys.events_type_newPerson },
  { value: 'updatePerson', labelKey: TranslationKeys.events_type_updatePerson },
  { value: 'deletePerson', labelKey: TranslationKeys.events_type_deletePerson },
] as const;
type EventsToolbarProps = {
  processEvents: () => void;
  processAllEvents: () => void;
  deleteSelectedEvents: () => void;
  hasSelectedItems: boolean;
  selectedStatus: string;
  selectedEmployer: string | null;
  selectedMutationType: string | null;
  employerOptions: Array<{ value: string; label: string }>;
  hideEmployerFilter?: boolean;
  onChangeFilter: (filter: { name: string; value: any }) => void;
  onSearch: (searchQuery: string) => void;
  intl: any;
};
const EventsToolbar = (props: EventsToolbarProps) => {
  const {
    processEvents,
    processAllEvents,
    deleteSelectedEvents,
    hasSelectedItems,
    selectedStatus,
    selectedEmployer,
    selectedMutationType,
    employerOptions,
    hideEmployerFilter = false,
    onChangeFilter,
    onSearch,
    intl,
  } = props;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const disabledIfNoAccessToEventEdit = !HasAccessTo(EVENT, EDIT);
  const dropdownOptions = {
    process: [
      {
        title: intl.formatMessage({ id: 'events.processAction' }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: 'events.processConfirmTitle' }),
          message: intl.formatMessage({ id: 'events.processConfirmMessage' }),
        },
        action: processEvents,
        disabled: disabledIfNoAccessToEventEdit || !hasSelectedItems,
      },
      {
        title: intl.formatMessage({ id: 'events.processAllAction' }),
        tooltip: intl.formatMessage({ id: 'tooltip_events_nothing_selected' }),
        confirmDialog: {
          title: intl.formatMessage({ id: 'events.processConfirmTitle' }),
          message: intl.formatMessage({ id: 'events.processAllConfirmMessage' }),
        },
        action: processAllEvents,
        disabled: disabledIfNoAccessToEventEdit,
      },
    ],
  };
  const handleStatusChange = React.useCallback(
    (event: any) => {
      onChangeFilter({ name: 'status', value: event.target.value });
    },
    [onChangeFilter],
  );
  const handleMutationTypeChange = React.useCallback(
    (event: any) => {
      onChangeFilter({ name: 'mutationType', value: event.target.value || null });
    },
    [onChangeFilter],
  );
  const handleEmployerChange = React.useCallback(
    (_event: React.SyntheticEvent, option: { value: string; label: string } | null) => {
      onChangeFilter({ name: 'employer', value: option?.value ?? null });
    },
    [onChangeFilter],
  );
  const handleSearch = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearch(event.target.value);
    },
    [onSearch],
  );
  const statusLabel = intl.formatMessage({ id: TranslationKeys.global_status });
  const employerLabel = intl.formatMessage({ id: TranslationKeys.global_employer });
  const mutationTypeLabel = intl.formatMessage({ id: TranslationKeys.global_mutationType });
  return (
    <DivRowStyled>
      <SearchInput onChange={handleSearch} style={{ marginRight: '8px' }} />
      <FormControlStyled>
        <InputLabel>{statusLabel}</InputLabel>
        <MuiSelect value={selectedStatus} onChange={handleStatusChange} label={statusLabel}>
          {PENSIOENBIJ_STATUS_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              <FormattedMessage id={opt.labelKey} />
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControlStyled>
      {!hideEmployerFilter && (
        <Autocomplete
          options={employerOptions}
          getOptionLabel={opt => opt.label}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          value={employerOptions.find(o => o.value === selectedEmployer) ?? null}
          onChange={handleEmployerChange}
          sx={{
            minWidth: '150px',
            marginRight: '5px',
            '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
              transform: 'translate(14px, 5px) scale(1)',
            },
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={employerLabel}
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            />
          )}
        />
      )}
      <FormControlStyled>
        <InputLabel>{mutationTypeLabel}</InputLabel>
        <MuiSelect value={selectedMutationType || ''} onChange={handleMutationTypeChange} label={mutationTypeLabel}>
          {MUTATION_TYPE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              <FormattedMessage id={opt.labelKey} />
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControlStyled>
      <SpacerStyled />
      {hasSelectedItems && (
        <ActionButton
          messageId={TranslationKeys.global_delete}
          onClick={() => setDeleteConfirmOpen(true)}
          color="error"
          variant="outlined"
          disabled={disabledIfNoAccessToEventEdit}
        />
      )}
      <ConfirmDialog
        title={<FormattedMessage id={TranslationKeys.pensioenbij_mutations_delete_confirmTitle} />}
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        onConfirm={deleteSelectedEvents}
      >
        <FormattedMessage id={TranslationKeys.pensioenbij_mutations_delete_confirmMessage} />
      </ConfirmDialog>
      <DropdownStyled options={dropdownOptions.process} intl />
    </DivRowStyled>
  );
};
EventsToolbar.propTypes = {
  processEvents: PropTypes.func.isRequired,
  processAllEvents: PropTypes.func.isRequired,
  deleteSelectedEvents: PropTypes.func.isRequired,
  hasSelectedItems: PropTypes.bool.isRequired,
  selectedStatus: PropTypes.string.isRequired,
  selectedEmployer: PropTypes.string,
  selectedMutationType: PropTypes.string,
  employerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  hideEmployerFilter: PropTypes.bool,
  onChangeFilter: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectIntl(EventsToolbar);
